<script setup lang="ts">
import { SCIcon, SCListItemRow, SCTag } from '@nsftx/seven-components'
import type { WalletTransaction } from '../interfaces/wallet/WalletTransaction'

// mock transactions data
// this will not represent real transactions interface/props
// later transactions data will be passed through props
// or based on wallet id fetching them from wallet store
const mockTransactionsData: WalletTransaction[] = [
  {
    money: 35.88,
    betType: 'Deposit',
    status: 'pending',
    date: '28.05.2025. | 16:40:48',
    transactionId: '03J9XIFJP99',
    afterBalance: 35.88,
    currency: 'EUR'
  },
  {
    money: 52.0,
    betType: 'Bet Placement',
    status: 'rejected',
    date: '28.05.2025. | 16:40:48',
    transactionId: '03J9XIFJP59',
    afterBalance: 35.88,
    currency: 'EUR'
  },
  {
    money: -37.34,
    betType: 'Bet Settlement',
    status: 'completed',
    date: '28.05.2025. | 16:40:48',
    transactionId: '03J9XIFJP92',
    afterBalance: 35.88,
    currency: 'EUR'
  }
]

const transactionLabelIcons = {
  success: {
    icon: 'n-i-arrow-up-a-ng',
    color: 'var(--sc-system-positive)'
  },
  warning: {
    icon: 'n-i-alert-ng',
    color: 'var(--sc-system-neutral)'
  },
  error: {
    icon: 'n-i-arrow-down-a-ng',
    color: 'var(--sc-system-negative)'
  }
}

// methods
const getTransactionIconProps = (transaction: WalletTransaction) => {
  if (transaction.status === 'rejected') {
    return transactionLabelIcons.warning
  }

  if (transaction.money > 0) {
    return transactionLabelIcons.success
  }

  return transactionLabelIcons.error
}

const getTransactionTagVariant = (transaction: WalletTransaction) => {
  switch (transaction.status) {
    case 'rejected':
      return 'error'
    case 'pending':
      return 'warning'
    case 'completed':
      return 'success'
    default:
      break
  }
}
</script>

<template>
  <div class="st-wallet-last-transactions st-d-flex-column sc-px-3 sc-bg-400">
    <div class="st-wallet-last-transactions__header st-d-flex-column sc-p-6">
      <h6 class="sc-text-h6 sc-text-primary-1 sc-mb-4">
        {{ $t('wallet_last_transactions_title') }}
      </h6>
      <p class="st-wallet-last-transactions__header__text">
        {{ $t('wallet_last_transactions_text') }}
      </p>
    </div>
    <div class="sc-bg-400">
      <div
        v-for="transaction in mockTransactionsData"
        :key="transaction.transactionId"
        class="sc-mb-3"
      >
        <SCListItemRow large background="var(--sc-theme-300)">
          <template #default>
            <div class="st-d-flex st-mr-auto">
              <div class="st-flex-center">
                <SCIcon
                  v-bind="getTransactionIconProps(transaction)"
                  size="2xl"
                  class="st-d-flex-center"
                />
              </div>
              <div class="st-d-flex-column sc-ml-6">
                <span
                  v-nxg-retail-format-number="{
                    value: transaction.money,
                    currency: transaction.currency
                  }"
                  class="sc-text-button sc-text-primary-1"
                />
                <span class="sc-text-caption-2 sc-text-primary-2">{{ transaction.betType }}</span>
              </div>
            </div>
            <SCTag
              :text="transaction.status"
              :variant="getTransactionTagVariant(transaction)"
              class="sc-mr-6"
            />
          </template>
          <template #expanded>
            <div class="st-w-100 st-d-flex-column sc-mb-2 sc-px-8">
              <div class="st-flex-center-between sc-mb-3">
                <div class="st-d-flex-column">
                  <span class="sc-text-caption-2 sc-text-primary-2">{{
                    $t('wallet_last_transactions_after_balance')
                  }}</span>
                  <span
                    v-nxg-retail-format-number="{
                      value: transaction.afterBalance,
                      currency: transaction.currency
                    }"
                    class="sc-text-body-1 sc-text-primary-1"
                  />
                </div>
                <div class="st-d-flex-column">
                  <span class="sc-text-caption-2 sc-text-primary-2">{{
                    $t('wallet_last_transactions_date')
                  }}</span>
                  <span class="sc-text-body-1 sc-text-primary-1">{{ transaction.date }}</span>
                </div>
              </div>
              <div class="st-flex-center-between">
                <div class="st-d-flex-column">
                  <span class="sc-text-caption-2 sc-text-primary-2">{{
                    $t('wallet_last_transactions_transaction_id')
                  }}</span>
                  <span class="sc-text-body-1 sc-text-primary-1">{{
                    transaction.transactionId
                  }}</span>
                </div>
              </div>
            </div>
          </template>
        </SCListItemRow>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.st-wallet-last-transactions {
  &__header {
    &__text {
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.37rem;
      color: var(--sc-text-primary-2);
    }
  }
}
</style>
